import JSZip from "jszip";
import { Message, Persona } from "../types";

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString();
}

function buildPersonaMap(personas: Persona[]): Map<string, string> {
  return new Map(personas.map((p) => [p.id, p.name]));
}

function formatMessagesForPersona(
  messages: Message[],
  persona: Persona,
): string {
  const filtered = messages.filter((m) => m.personaId === persona.id);

  return filtered
    .map((msg) => {
      const sender = msg.role === "user" ? "User" : persona.name;
      const ts = formatTimestamp(msg.timestamp);
      let line = `${sender} [${ts}]: ${msg.text}`;

      if (msg.role !== "user" && msg.filePaths && msg.filePaths.length > 0) {
        line += `\nFiles Revealed: ${msg.filePaths.join(", ")}`;
      }

      return line;
    })
    .join("\n\n");
}

function formatAllMessages(messages: Message[], personas: Persona[]): string {
  const personaMap = buildPersonaMap(personas);

  return messages
    .map((msg) => {
      const ts = formatTimestamp(msg.timestamp);

      if (msg.role === "user") {
        const directedTo = personaMap.get(msg.personaId) ?? msg.personaId;
        let line = `User [${ts}]: ${msg.text}`;
        line += `\nDirected to: ${directedTo}`;
        return line;
      }

      const personaName = personaMap.get(msg.personaId) ?? msg.personaId;
      let line = `${personaName} [${ts}]: ${msg.text}`;

      if (msg.filePaths && msg.filePaths.length > 0) {
        line += `\nFiles Revealed: ${msg.filePaths.join(", ")}`;
      }

      return line;
    })
    .join("\n\n");
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportSessionAsZip(
  messages: Message[],
  personas: Persona[],
  sessionId: string,
): Promise<void> {
  const zip = new JSZip();

  for (const persona of personas) {
    const formatted = formatMessagesForPersona(messages, persona);
    zip.file(`Conversation-with-${persona.name}.txt`, formatted);
  }

  const content = await zip.generateAsync({ type: "blob" });
  triggerDownload(content, `pinstripe-session-${sessionId}.zip`);
}

export function exportSessionAsFile(
  messages: Message[],
  personas: Persona[],
  sessionId: string,
): void {
  const formatted = formatAllMessages(messages, personas);
  const blob = new Blob([formatted], { type: "text/plain" });
  triggerDownload(blob, `pinstripe-session-${sessionId}.txt`);
}
